import { query, mutate } from '@sylchi/keystone-graphql-client'
import Router from 'src/router/index'
import Store from '.'
import { campaignFullDetails } from './fragments'
import dateformat from 'dateformat'

export default {
  login: async ({ state, commit, dispatch }, { email, password, mfaKey }) => {
    let problems = false;
    try{
      const { data, errors } = await mutate({
        mutation: `
          mutation($email: String!, $password: String!) {
              user: authenticateUserWithPassword(email: $email, password: $password) {
                item{
                  id
                }
              }
          }
        `,
        variables: { email, password, mfaKey }
      });
      problems = errors;
      if(data?.user){
        return await dispatch('getAuthenticatedUser');
      }
    } catch (err) {
      console.error(err);
    }
    return problems;
  },
  register: async ({ dispatch }, data) => {
    try{
      const { data: { user } } = await mutate({
        mutation: `
          mutation($data: UserCreateInput!){
              user: createUser(data: $data){
                id
              }
          }
        `,
        variables: { data }
      });
      if(user){
        return dispatch('login', { email: data.email, password: data.password });
      }
    } catch (err) {
      console.error(err);
    }
    return false;
  },
  getAuthenticatedUser: async ({ commit }) => {
    try {
      const thisMonthStart = new Date();
      thisMonthStart.setDate(1);

      const now = new Date();

      const lastMonthEnd = new Date();
      lastMonthEnd.setDate(0);

      const lastMonthStart = new Date(lastMonthEnd.getTime());
      lastMonthStart.setDate(1);

      const formatted = (date) => dateformat(date, 'UTC:yyyy-mm-dd');
      
      const { data } = await query({
        query: `
          query{
              authenticatedUser{
                id
                name
                email
                userLevel
                brandName
                legalType
                registryCode
                taxId
                iban
                campaignSettings{
                  id
                  campaign{
                    id
                  }
                  campaignModel{
                    value: id
                    label: name
                  }
                  pixelUrl
                }
                country{
                  id
                  name
                }
                city
                street
                zip
                notifications
                pixelUrl
                preferredMessenger
                messengerUserName
                mfaEnabled
                priceGroup
                statsToday: stats(startDate: "${formatted(now)}", endDate: "${formatted(now)}"){
                  clicks
                  conversions
                  earnings
                }
                statsThisMonth: stats(startDate: "${formatted(thisMonthStart)}", endDate: "${formatted(now)}"){
                  clicks
                  conversions
                  earnings
                }
                statsLastMonth: stats(startDate: "${formatted(lastMonthStart)}", endDate: "${formatted(lastMonthEnd)}"){
                  clicks
                  conversions
                  earnings
                }
                currentBalance
                createdAt
              }
          }
        `
      });
      commit('setUserData', data.authenticatedUser);
      if(Store.state.returnRoute) {
        Router.push(Store.state.returnRoute.path);
        Store.commit('setReturnRoute', null);
      }
      return data?.authenticatedUser !== null;
    } catch (err) {
      console.error(err);
    }
    return false;
  },
  fetchCampaigns: async ({ commit }) => {
    const { data } = await query({
      query: `
        query{
            allCampaigns(sortBy: status_ASC){
              id
              name
              status
              targetCountries{
                iso2
              }
              campaignImage{
                publicUrl
              }
              userPrices{
                model
                price
              }
              stats{
                cr
                epc
              }
            }
        }
      `
    });
    commit('setCampaigns', data.allCampaigns);
  },
  logout: async ({ commit }) => {
     try{
       const { data } = await mutate({
         mutation: `
           mutation{
            unauthenticateUser{
              success
            }
           }
         `
       });
       if(data?.unauthenticateUser){
        commit('setUserData', null);
        window.location.href = '/login';
        return true;
       }
     } catch (err){
       console.error(err);
     } 
     return false;
  },
  fetchCampaign: async ({ commit }, { id }) => {
    try{
      const monthAgo = new Date();
      monthAgo.setDate(monthAgo.getDate()-30);
      const startDate = dateformat(monthAgo, 'UTC:yyyy-mm-dd');
      const endDate = dateformat(Date.now(), 'UTC:yyyy-mm-dd');
      const { data } = await query({
        query: `
          query{
           Campaign(where: { id: "${id}" } ) {
              ...campaignFullDetails
              stats(startDate: "${startDate}", endDate: "${endDate}"){
                cr
                epc
              }
            }
          }
          ${campaignFullDetails}
        `
      });
      if(data?.Campaign){
       commit('setCampaign', data.Campaign);
       return true;
      }
    } catch (err){
      console.error(err);
    } 
    return false;
  },
  fetchUserAggregations: async ({ state, commit, dispatch }, conversionTypes=[]) => {
      if(state.selectedCampaigns.length > 0) dispatch('fetchClicksByCampaign')
      try{
        const { data } = await query({
          query: `
            query{
              allUserDayAggregations(startDate: "${state.dateFrom}", endDate: "${state.dateTo}", user: "${state.user.userLevel === 'ADMIN' && state.selectedUser ? state.selectedUser.value : state.user.id }" ) {
                clicks
                conversions
                earnings
                conversionRate
                epc
                date
              }
            }
          `
        });
        commit('setUserAggregations', data.allUserDayAggregations);
        if(state.selectedCampaigns.length === 0) commit('setClicksByCampaign', data.allUserDayAggregations.reduce((a,b) => a+=b.clicks, 0))
        return;
      } catch (err) {
        console.error(err);
      }
  },
  fetchClicksByCampaign: async ({ state, commit }) => {
      const variables = {
        data: {
          createdAt_gte: new Date(new Date(state.dateFrom).setUTCHours(0,0,0,0)).toISOString(),
          createdAt_lt: new Date(new Date(state.dateTo).setUTCHours(24)).toISOString(),
          publisher: { id: state.user.userLevel === 'ADMIN' && state.selectedUser ? state.selectedUser.value : state.user.id },
        }
      }
      if(state.selectedCampaigns.length > 0){
        if(state.selectedCampaigns.length === 1){
          variables.data.campaign = { id: state.selectedCampaigns[0].value }
        } else {
          variables.data.campaign = { id_in: state.selectedCampaigns.map(item => item.value) }
        }
      }
      try{
        const { data } = await query({
          query: `
            query($data: ClickWhereInput!){
              _allClicksMeta(where: $data){
                count
              }
            }
          `,
          variables
        });
        commit('setClicksByCampaign', data._allClicksMeta.count);
      } catch (err) {
        console.error(err);
      }
  },
  fetchUserConversions: async ({ state, commit }, conversionTypes=[]) => {
    const variables = {
      data: {
        createdAt_gte: new Date(new Date(state.dateFrom).setUTCHours(0,0,0,0)).toISOString(),
        createdAt_lt: new Date(new Date(state.dateTo).setUTCHours(24)).toISOString(),
        publisher: { id: state.user.userLevel === 'ADMIN' && state.selectedUser ? state.selectedUser.value : state.user.id },
      }
    }
    if(state.selectedCampaigns.length > 0){
      if(state.selectedCampaigns.length === 1){
        variables.data.campaign = { id: state.selectedCampaigns[0].value }
      } else {
        variables.data.campaign = { id_in: state.selectedCampaigns.map(item => item.value) }
      }
    }
    if(conversionTypes.length > 0){
      variables.data.campaignModel_in = conversionTypes.map(item => item.toLowerCase());
    }
    try{
      const { data } = await query({
        query: `
          query($data: ConversionWhereInput!){
            allConversions(where: $data, sortBy: createdAt_DESC){
              id
              createdAt
              campaign{
                name
              }
              click{
                id
                urlParams
              }
              campaignModel
              publisherPrice
              publisherStatus              
            }
          }
        `,
        variables
      });
      commit('setUserConversions', data.allConversions);
      return;
    } catch (err) {
      console.error(err);
    }
  },
  fetchNotifications: async ({ state, commit }) => {
    try{
      const { data } = await query({
        query: `
          query($data: NotificationWhereInput!){
            allNotifications(where: $data, sortBy: createdAt_DESC){
              id
              title: name
              content
              createdAt
              campaigns{
                id
                name
              }
            }
          }
        `,
        variables: {
          data: {}
        }
      });
      commit('setNotifications', data.allNotifications);
    } catch (err) {
      console.error(err);
    }
  }
};
  